import {
  DatabaseProjectDraft,
  ProjectDraftMainBuilding,
  ProjectDraftBuilding,
  ProjectDraftCompleted,
  ProjectBuilding,
  BuildingInformation,
  NewProjectData,
  EnterpriseProjectObjectProduct,
} from '@/features/core/store/page-modules/enterprise-project-create/create-module-types';
import {
  defaultUseCaseOptions,
  EnterpriseProjectObjectUseCase,
} from '@/features/app-enterprise-project/enterprise-project-constants';
import structuredClone from '@ungap/structured-clone';

export function cleanLatLong(val: number): number {
  const strVal = val.toString();
  return Number(strVal.replace(',', ''));
}

export function sortProjectDraftBuildings(buildings: ProjectBuilding[]): ProjectBuilding[] {
  // Helper function to extract number from a string
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const extractNumber = (str: string) => parseInt(/\d+/.exec(str)?.[0] || '0', 10);
  return buildings.sort((a, b) => {
    // Split the building name into parts
    const [streetA, houseNumberA, addressSupplementA] = a.name.split(' ');
    const [streetB, houseNumberB, addressSupplementB] = b.name.split(' ');

    // First, compare by street
    const streetComparison = streetA.localeCompare(streetB);
    if (streetComparison !== 0) return streetComparison;

    // Then, compare by house number
    const numA = extractNumber(houseNumberA);
    const numB = extractNumber(houseNumberB);
    if (numA !== numB) return numA - numB;

    // Finally, compare by addressSupplement
    return (addressSupplementA || '').localeCompare(addressSupplementB || '');
  });
}

// Convert the useCase monetary values to numbers
export function convertUseCaseMonetaryValues(useCases: ProjectBuilding['useCases']): ProjectBuilding['useCases'] {
  if (!useCases) return [];
  return useCases.map((useCase) => {
    return {
      ...useCase,
      monthlyPriceInEuros: useCase.monthlyPriceInEuros
        ? parseFloat(useCase.monthlyPriceInEuros.toString().replace(',', '.'))
        : null,
      oneTimePriceInEuros: useCase.oneTimePriceInEuros
        ? parseFloat(useCase.oneTimePriceInEuros.toString().replace(',', '.'))
        : null,
    };
  });
}

function validateEmail(email: string): boolean {
  // Regular expression pattern for email validation
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Test the email against the pattern
  return emailPattern.test(email);
}

function validateTextField(text: string | null | undefined): boolean {
  return text !== null && text !== undefined && text.length > 0;
}

function validateLiegenshaftContactInformation(contactInformation: BuildingInformation | undefined): boolean {
  if (!contactInformation) return false;
  return (
    validateEmail(contactInformation.email) &&
    validateTextField(contactInformation.phoneNumber) &&
    validateTextField(contactInformation.firstName) &&
    validateTextField(contactInformation.surname) &&
    validateTextField(contactInformation.title)
  );
}

// We check if the parent building has an elevator product. If the building is the parent building
// we still just check the building itself. If there is no elevator product, then we set the building
// "hasElevator" to false.
function removeInvalidHasElevatorValues(building: ProjectBuilding, buildings: ProjectBuilding[]): void {
  const checkBuilding = (b: ProjectBuilding): boolean => {
    if (b.useCases) {
      for (const useCase of b.useCases) {
        if (
          useCase.useCaseType === EnterpriseProjectObjectUseCase.SMART_ELEVATOR_MAINTENANCE ||
          useCase.useCaseType === EnterpriseProjectObjectUseCase.ELEVATOR_MONITORING
        ) {
          return true;
        }
      }
    }
    return false;
  };

  if (building.parentProperty) {
    for (const b of buildings) {
      if (b.id === building.parentProperty) {
        if (!checkBuilding(b)) {
          building.hasElevator = false;
          return;
        }
      }
    }
  } else {
    if (!checkBuilding(building)) {
      building.hasElevator = false;
      return;
    }
  }
}

export function transformStateForSaving(object: NewProjectData): DatabaseProjectDraft | null {
  const mainBuildings: ProjectDraftMainBuilding[] = [];
  const buildings: ProjectDraftBuilding[] = [];

  // First we create the main buildings (only properties)
  for (let i = 0; i < object.buildings.length; i++) {
    const building = object.buildings[i];
    // Check the elevators
    removeInvalidHasElevatorValues(building, object.buildings);
    if (building.liegenshaft) {
      // Convert the useCase monetary values to numbers
      const mainBuilding: ProjectDraftMainBuilding = {
        id: building.buildingId,
        name: building.name,
        cityAddress: building.cityAddress,
        houseAddress: building.houseAddress,
        houseAddressSuffix: building.houseAddressSuffix,
        streetAddress: building.streetAddress,
        postCodeAddress: building.postCodeAddress,
        locationLongitude: cleanLatLong(building.locationLongitude),
        locationLatitude: cleanLatLong(building.locationLatitude),
        numberOfApartments: building.numberOfApartments,
        customerContact: building.liegenshaftInformation,
        mainService: building.mainService,
        products: building.products ?? [],
        useCases: building.useCases ?? [],
        invoiceName: building.invoiceName,
        serviceLevel: building.serviceLevel,
        installationStartDate: building.installationStartDate,
        preInspection: building.preInspection,
        wieNumber: building.wieNumber,
        hasElevator: building.hasElevator,
        includesComments: building.includesComments ?? false,
        comment: building.comment,
        objectId: building.objectId,
      };
      mainBuildings.push(mainBuilding);
    }
    if (!building.liegenshaft) {
      const newBuilding: ProjectDraftBuilding = {
        id: building.buildingId,
        mainBuildingId: building.parentProperty ? building.parentProperty : null,
        originalMainBuildingId: building.originalMainBuildingId ? building.originalMainBuildingId : null,
        name: building.name,
        cityAddress: building.cityAddress,
        houseAddress: building.houseAddress,
        houseAddressSuffix: building.houseAddressSuffix,
        streetAddress: building.streetAddress,
        postCodeAddress: building.postCodeAddress,
        locationLongitude: cleanLatLong(building.locationLongitude),
        locationLatitude: cleanLatLong(building.locationLatitude),
        numberOfApartments: building.numberOfApartments,
        hasElevator: building.hasElevator,
        comment: building.comment,
        objectId: building.objectId,
      };
      buildings.push(newBuilding);
    }
  }

  if (object.id && object.projectReference) {
    return {
      id: object.id,
      projectReference: object.projectReference,
      parentProjectId: object.parentProjectId,
      projectType: object.projectType,
      general: object.general,
      customerInfo: {
        customerName: object.general.customerName,
        customerNumber: object.general.customerNumber,
        contactPerson: object.general.partnerName,
        invoiceNumber: object.general.invoiceNumber,
        contractTerm: object.general.contractTerm,
        createNewInvoiceNumber: object.general.createNewInvoiceNumber,
        referenceToMainContract: object.general.referenceToMainContract,
        transferToMainContract: object.general.transferToMainContract,
        comment: object.general.comment,
      },
      mainBuildings,
      buildings,
    };
  }

  return null;
}

function transformBuildings(object: DatabaseProjectDraft & ProjectDraftCompleted): ProjectBuilding[] {
  const buildings: ProjectBuilding[] = [];

  for (let i = 0; i < object.buildings.length; i++) {
    buildings.push({
      id: object.buildings[i].id,
      buildingId: object.buildings[i].id,
      name: object.buildings[i].name,
      cityAddress: object.buildings[i].cityAddress,
      houseAddress: object.buildings[i].houseAddress,
      houseAddressSuffix: object.buildings[i].houseAddressSuffix,
      streetAddress: object.buildings[i].streetAddress,
      postCodeAddress: object.buildings[i].postCodeAddress,
      locationLongitude: object.buildings[i].locationLongitude,
      locationLatitude: object.buildings[i].locationLatitude,
      numberOfApartments: object.buildings[i].numberOfApartments,
      liegenshaft: false,
      liegenshaftInformation: undefined,
      parentProperty: object.buildings[i].mainBuildingId,
      originalMainBuildingId: object.buildings[i].originalMainBuildingId,
      hasElevator: object.buildings[i].hasElevator,
      comment: object.buildings[i].comment,
      objectId: object.buildings[i].objectId,
    });
  }

  for (let i = 0; i < object.mainBuildings.length; i++) {
    buildings.push({
      id: object.mainBuildings[i].id,
      buildingId: object.mainBuildings[i].id,
      name: object.mainBuildings[i].name,
      cityAddress: object.mainBuildings[i].cityAddress,
      houseAddress: object.mainBuildings[i].houseAddress,
      houseAddressSuffix: object.mainBuildings[i].houseAddressSuffix,
      streetAddress: object.mainBuildings[i].streetAddress,
      postCodeAddress: object.mainBuildings[i].postCodeAddress,
      locationLongitude: object.mainBuildings[i].locationLongitude,
      locationLatitude: object.mainBuildings[i].locationLatitude,
      numberOfApartments: object.mainBuildings[i].numberOfApartments,
      liegenshaft: true,
      liegenshaftInformation: object.mainBuildings[i].customerContact,
      mainService: object.mainBuildings[i].mainService,
      // Invoice name, service level, installation start date, and pre-inspection
      invoiceName: object.mainBuildings[i].invoiceName,
      serviceLevel: object.mainBuildings[i].serviceLevel,
      installationStartDate: object.mainBuildings[i].installationStartDate,
      preInspection: object.mainBuildings[i].preInspection,
      hasElevator: object.mainBuildings[i].hasElevator,
      products: object.mainBuildings[i].products || [
        {
          monthlyPriceInEuros: null,
          oneTimePriceInEuros: null,
          productType: EnterpriseProjectObjectProduct.INFRASTRUCTURE_VARIANT,
          option: null,
        },
        {
          monthlyPriceInEuros: null,
          oneTimePriceInEuros: null,
          productType: EnterpriseProjectObjectProduct.IW_CLOUD,
          option: null,
        },
      ],
      useCases: object.mainBuildings[i].useCases || structuredClone(defaultUseCaseOptions),
      liegenshaftInformationCompleted: validateLiegenshaftContactInformation(object.mainBuildings[i].customerContact),
      wieNumber: object.mainBuildings[i].wieNumber,
      comment: object.mainBuildings[i].comment,
      includesComments: object.mainBuildings[i].includesComments,
      objectId: object.mainBuildings[i].objectId,
    });
  }

  return buildings;
}

export function transformDbObjectForState(
  object: DatabaseProjectDraft & ProjectDraftCompleted,
): NewProjectData & ProjectDraftCompleted {
  let buildings = transformBuildings(object);

  // Sort buildings by street, house number, and addressSupplement
  buildings = sortProjectDraftBuildings(buildings);

  return {
    id: object.id,
    projectReference: object.projectReference,
    projectType: object.projectType,
    parentProjectId: object.parentProjectId,
    general: object.general,
    customerInfo: object.customerInfo,
    buildings,
    generalInformationCompleted: object.generalInformationCompleted ? object.generalInformationCompleted : false,
    buildingsImportCompleted: object.buildingsImportCompleted ? object.buildingsImportCompleted : false,
    propertyInformationCompleted: object.propertyInformationCompleted ? object.propertyInformationCompleted : false,
    propertyAssociationCompleted: object.propertyAssociationCompleted ? object.propertyAssociationCompleted : false,
    originalMainBuildingIds: object.originalMainBuildingIds ? object.originalMainBuildingIds : [],
    originalBuildingIds: object.originalBuildingIds ? object.originalBuildingIds : [],
    omittedBuildingIds: object.omittedBuildingIds ? object.omittedBuildingIds : [],
  };
}

// Helper function to compare arrays
function arraysEqual(a: any[], b: any[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (!deepEqual(a[i], b[i])) {
      return false;
    }
  }
  return true;
}

// Helper function to handle deep equality check
function deepEqual(a: any, b: any): boolean {
  if (a === b) return true;

  // Check if both are arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    return arraysEqual(a, b); // Compare arrays
  }

  // Check if both are objects
  if (typeof a === 'object' && typeof b === 'object' && a !== null && b !== null) {
    return !hasObjectChanged(a, b); // Recursively compare objects
  }

  return false; // Primitive values or type mismatch
}

// Main function to compare objects
export function hasObjectChanged<T>(original: T, updated: Partial<T>): boolean {
  // Compare all keys in the original object
  for (const key in original) {
    if (Object.prototype.hasOwnProperty.call(original, key)) {
      if (!deepEqual(original[key], updated[key])) {
        return true; // Change detected
      }
    }
  }

  // Compare all keys in the updated object
  for (const key in updated) {
    if (Object.prototype.hasOwnProperty.call(updated, key)) {
      if (!deepEqual(original[key], updated[key])) {
        return true; // Change detected
      }
    }
  }

  return false; // No differences found
}

export function projectHasElevatorProduct(buildings: ProjectBuilding[]): boolean {
  // Check the main buildings for elevator use case
  for (const building of buildings) {
    if (building.liegenshaft && building.useCases) {
      for (const useCase of building.useCases) {
        if (
          useCase.useCaseType === EnterpriseProjectObjectUseCase.SMART_ELEVATOR_MAINTENANCE ||
          useCase.useCaseType === EnterpriseProjectObjectUseCase.ELEVATOR_MONITORING
        ) {
          return true;
        }
      }
    }
  }

  return false;
}
